'use client'

import { useContext, useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { MdOutlineMenu, MdOutlineClose, MdOutlineArrowForward, MdSmartToy } from 'react-icons/md'
import { ThemeContext } from './ThemeContext';
import { AnimatedThemeToggler } from './ui/themeToggler';
// Use absolute path - CRA automatically serves files from public folder
const speakVideo = '/speak4.mp4';

const AIGuide = ({ onDismiss, isVisible, buttonRef, onStartChat }) => {
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (isVisible && videoRef.current) {
      const timer = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(err => console.error("AIGuide video play error:", err));
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;

      const updateCoords = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const isMobileView = window.innerWidth < 768;
          setIsMobile(isMobileView);
          setCoords({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
        }
      };

      updateCoords();
      window.addEventListener('resize', updateCoords);

      return () => {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "0";
        window.removeEventListener('resize', updateCoords);
      };
    }
  }, [isVisible, buttonRef]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99998] pointer-events-auto bg-black/60 backdrop-blur-[2px] touch-none"
            onClick={onDismiss}
            style={!isMobile ? {
              clipPath: `polygon(
                0% 0%, 
                0% 100%, 
                ${coords.left - 10}px 100%, 
                ${coords.left - 10}px ${coords.top - 10}px, 
                ${coords.left + coords.width + 10}px ${coords.top - 10}px, 
                ${coords.left + coords.width + 10}px ${coords.top + coords.height + 10}px, 
                ${coords.left - 10}px ${coords.top + coords.height + 10}px, 
                ${coords.left - 10}px 100%, 
                100% 100%, 
                100% 0%
              )`,
            } : {}}
          />

          <div
            className={`fixed z-[99999] pointer-events-none flex flex-col items-center ${isMobile ? 'inset-0 justify-center px-4' : ''}`}
            style={!isMobile ? {
              top: coords.top + coords.height + 20,
              left: coords.left + coords.width / 2,
              transform: 'translateX(-50%)',
            } : {}}
          >
            <motion.div
              initial={{ opacity: 0, y: isMobile ? 20 : -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: isMobile ? 20 : -10, scale: 0.95 }}
              className="pointer-events-auto bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4 sm:p-5 rounded-xl sm:rounded-2xl shadow-2xl w-[260px] sm:w-[300px] md:w-[340px] font-sans overflow-hidden flex flex-col items-center text-center gap-3"
            >
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border-2 border-blue-500 shadow-lg shadow-blue-500/20 flex-shrink-0">
                <video
                  ref={videoRef}
                  src={speakVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="w-full h-full object-cover scale-100"
                  style={{ objectPosition: 'center 15%' }}
                  onError={(e) => console.error('AIGuide video error:', e)}
                />
              </div>

              <div className="space-y-1">
                <h3 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100">Talk with my AI</h3>
                <p className="text-zinc-500 dark:text-zinc-400 text-xs sm:text-sm">
                  Ask me anything about my projects!
                </p>
                <span className="text-xs text-center text-blue-700 dark:text-blue-300">
                  Turn on your mic to talk with AI in realtime
                </span>
              </div>

              {/* Mic Note */}


              <div className={`w-full lg:space-y-0 space-y-2 ${!isMobile ? 'flex items-center flex-row gap-2' : ''}`}>
                <button
                  onClick={() => {
                    onStartChat();
                  }}
                  className={`${!isMobile ? 'flex-1' : 'w-full'} bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 sm:py-2.5 rounded-lg sm:rounded-xl transition-all text-xs sm:text-sm shadow-md shadow-blue-500/20`}
                >
                  Start Chat
                </button>
                <button
                  onClick={onDismiss}
                  className={`${!isMobile ? 'flex-1' : 'w-full'} bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-600 dark:text-zinc-300 font-semibold py-2 sm:py-2.5 rounded-lg sm:rounded-xl transition-all text-xs sm:text-sm`}
                >
                  Skip
                </button>
              </div>

              {!isMobile && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-zinc-900 border-t border-l border-zinc-200 dark:border-zinc-800 rotate-45" />
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

const MicGuide = ({ isVisible, onDismiss, buttonRef }) => {
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (isVisible) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;

      const updateCoords = () => {
        if (buttonRef?.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const isMobileView = window.innerWidth < 768;
          setIsMobile(isMobileView);
          setCoords({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
        }
      };

      updateCoords();
      window.addEventListener('resize', updateCoords);

      return () => {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "0";
        window.removeEventListener('resize', updateCoords);
      };
    }
  }, [isVisible, buttonRef]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99997] pointer-events-auto bg-black/40 touch-none"
            onClick={onDismiss}
          />

          <div className="fixed z-[99998] pointer-events-none inset-0">
            {/* Pointer card - positioned relative to mic button */}
            {isMobile ? (
              // Mobile: below the button
              <div
                className="fixed pointer-events-none"
                style={{
                  top: `${coords.top + coords.height + 15}px`,
                  left: `${coords.left + coords.width / 2}px`,
                  transform: 'translateX(-50%)',
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.9 }}
                  className="pointer-events-auto bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-xl w-[240px] p-3 flex items-start gap-2.5"
                >
                  <span className="text-2xl flex-shrink-0 mt-0.5">🎤</span>
                  <div className="space-y-1">
                    <p className="text-xs font-bold text-zinc-900 dark:text-zinc-100">
                      Enable Mic
                    </p>
                    <p className="text-xs text-zinc-600 dark:text-zinc-400">
                      Turn on your mic for realtime AI conversation
                    </p>
                  </div>
                  {/* Pointer arrow for mobile */}
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white dark:bg-zinc-900 border-t border-l border-zinc-200 dark:border-zinc-800 rotate-45" />
                </motion.div>

                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  onClick={onDismiss}
                  className="mt-3 mx-auto block text-xs font-semibold text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
                >
                  Got it!
                </motion.button>
              </div>
            ) : (
              // Desktop: to the right of the button
              <div
                className="fixed pointer-events-none"
                style={{
                  top: `${coords.top + coords.height / 2}px`,
                  left: `${coords.left + coords.width + 20}px`,
                  transform: 'translateY(-50%)',
                }}
              >
                <motion.div
                  initial={{ opacity: 0, x: -10, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -10, scale: 0.9 }}
                  className="pointer-events-auto bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-xl p-3 w-[260px] flex items-start gap-2.5 relative"
                >
                  <span className="text-2xl flex-shrink-0 mt-0.5">🎤</span>
                  <div className="space-y-1">
                    <p className="text-xs font-bold text-zinc-900 dark:text-zinc-100">
                      Enable Mic
                    </p>
                    <p className="text-xs text-zinc-600 dark:text-zinc-400">
                      Click to talk with AI in realtime
                    </p>
                  </div>
                  {/* Pointer arrow for desktop */}
                  <div className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-white dark:bg-zinc-900 border-b border-r border-zinc-200 dark:border-zinc-800 rotate-45" />
                </motion.div>

                <motion.button
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  onClick={onDismiss}
                  className="absolute left-0 top-full mt-3 text-xs font-semibold text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors whitespace-nowrap"
                >
                  Got it!
                </motion.button>
              </div>
            )}
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

function Header({ onLetsTalk, isMenuOpen, setIsMenuOpen, isAISidebarOpen }) {
  const [showAIGuide, setShowAIGuide] = useState(false);
  const [showMicGuide, setShowMicGuide] = useState(false);
  const aiButtonRef = useRef(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const hasInteracted = localStorage.getItem('has_interacted_with_ai');
    if (!hasInteracted) {
      const timer = setTimeout(() => {
        setShowAIGuide(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAIInteraction = () => {
    setShowAIGuide(false);
    localStorage.setItem('has_interacted_with_ai', 'true');

    // Open the sidebar first
    if (onLetsTalk) onLetsTalk();
  };

  // Show mic guide only when sidebar is open for the first time
  useEffect(() => {
    if (isAISidebarOpen) {
      const hasOpenedSidebarBefore = localStorage.getItem('first_time_sidebar_opened');
      if (!hasOpenedSidebarBefore) {
        // Show mic guide after 1 second when sidebar opens
        setTimeout(() => {
          setShowMicGuide(true);
        }, 1000);
        localStorage.setItem('first_time_sidebar_opened', 'true');
      }
    } else {
      // Hide mic guide when sidebar closes
      setShowMicGuide(false);
    }
  }, [isAISidebarOpen]);

  const handleDismiss = () => {
    setShowAIGuide(false);
    setShowMicGuide(false);
    localStorage.setItem('has_interacted_with_ai', 'true');
  };

  const handleMicGuideDismiss = () => {
    setShowMicGuide(false);
    localStorage.setItem('has_seen_mic_guide', 'true');
    // Now open the sidebar after dismissing guide
    if (onLetsTalk) onLetsTalk();
  };

  const menuItems = [
    { title: "Home", number: "01", href: "#home" },
    { title: "About Me", number: "02", href: "#about" },
    { title: "Experience & Projects", number: "03", href: "#works" },
    { title: "Tools", number: "04", href: "#insights" },
  ];

  const { isLight, handleMode } = useContext(ThemeContext);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleScroll = (id) => {
    const element = document.querySelector(id);
    toggleMenu()
    if (element) {
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementTop - 120, // Adjust the top by your desired offset
        behavior: "smooth", // Smooth scroll effect
      });

    };
  }

  return (
    <header className="lg:block fixed hidden top-0 left-0 right-0 z-50 py-4 bg-zinc-50 dark:bg-black dark:bg-opacity-95 ">
      <div className="max-w-8xl mx-auto px-4 lg:px-28">
        <div className="flex  items-center justify-between  lg:h-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <motion.a
              href="/"
              className="text-2xl tracking-wide relative dark:text-zinc-400 text-zinc-900 font-medium"
            >
              vivek
              <span className="absolute -left-3 top-0.5 w-8 h-8 rounded-full dark:bg-zinc-800 bg-zinc-300 -z-10" />

              <span className="dark:text-zinc-100 text-zinc-400 tracking-wide">Khule</span>.dev
            </motion.a>
          </motion.div>
          <motion.div
            className="flex items-center gap-4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {/* AI Guide Portal */}
            <AIGuide
              isVisible={showAIGuide}
              onDismiss={handleDismiss}
              onStartChat={handleAIInteraction}
              buttonRef={aiButtonRef}
            />

            {/* Mic Guide Portal - Points to mic button */}
            {/* <MicGuide
              isVisible={showMicGuide}
              onDismiss={handleMicGuideDismiss}
              buttonRef={aiButtonRef}
            /> */}

            <motion.button
              ref={aiButtonRef}
              onClick={handleAIInteraction}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={{
                boxShadow: [
                  "0 0 0px rgba(59, 130, 246, 0)",
                  "0 0 20px rgba(59, 130, 246, 0.3)",
                  "0 0 0px rgba(59, 130, 246, 0)"
                ]
              }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="group relative text-lg items-center px-4 py-2 rounded-full border-2 dark:border-zinc-700 border-zinc-200 overflow-hidden bg-white dark:bg-zinc-900 dark:text-zinc-100 text-zinc-900 transition-all hover:border-zinc-400 dark:hover:border-zinc-500 shadow-md inline-flex"
            >
              {/* Continuous Shimmer Animation */}
              <div className="animate-shimmer z-0 pointer-events-none" />

              <div className="relative z-10 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full overflow-hidden border border-zinc-200 dark:border-zinc-700 bg-zinc-100 shrink-0 relative">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                    className="w-full h-full"
                  >
                    <img src={require('../assets/ailogo.png')} alt="AI" className="w-full h-full object-cover" />
                  </motion.div>
                </div>
                <span className="hidden md:inline-block font-bold tracking-tight bg-gradient-to-r from-zinc-900 to-zinc-600 dark:from-white dark:to-zinc-400 bg-clip-text text-transparent">
                  My AI
                </span>
                <MdOutlineArrowForward className="hidden md:block w-4 h-4 group-hover:translate-x-1 transition-transform opacity-60" />
              </div>
            </motion.button>

            {/* Theme Toggle */}
            <AnimatedThemeToggler />


            {/* Menu Button */}
            <motion.button
              className="lg:p-2 rounded-full dark:hover:bg-zinc-600 hover:bg-zinc-300 transition-colors"
              aria-label="Menu"
              onClick={toggleMenu}
            >
              {isMenuOpen ? (
                <MdOutlineClose className="w-8 h-8 dark:text-zinc-100 text-zinc-900" />
              ) : (
                <MdOutlineMenu className="w-8 h-8 dark:text-zinc-100 text-zinc-900" />
              )}
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Fullscreen Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="fixed inset-0 top-16 lg:top-28 dark:bg-black bg-zinc-50 z-40 overflow-y-auto scrollbar-hide"
          >
            <div className="max-w-8xl mx-auto px-4 lg:px-20 min-h-[calc(100vh-6rem)] flex flex-col gap-14 justify-between">
              <nav className="space-y-8 sm:space-y-10 lg:space-y-12 border-y-[1.5px] py-8 sm:py-10 dark:border-zinc-500 border-zinc-900">
                {menuItems.map((item, index) => (
                  <motion.div
                    key={item.number}
                    className="group"
                  >
                    <a
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault(); // Prevent the default link behavior
                        handleScroll(item.href); // Trigger scroll behavior
                      }}
                      className="flex items-center justify-between group relative"
                    >
                      <div className="flex items-baseline gap-4">
                        <span className="text-3xl sm:text-4xl lg:text-7xl font-bold dark:text-white text-zinc-900 dark:hover:text-gray-300 hover:text-zinc-800 transition-colors">
                          {item.title}
                        </span>
                        <span className="text-base sm:text-lg lg:text-xl dark:text-gray-500 text-zinc-950">
                          ({item.number})
                        </span>
                      </div>
                      <div className="relative">
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className="w-12 h-12 rounded-full flex items-center justify-center dark:group-hover:bg-gray-800 group-hover:bg-gray-300 transition-colors"
                        >
                          <MdOutlineArrowForward className="w-6 h-6 dark:text-white text-zinc-900 transform rotate-45" />
                        </motion.div>
                      </div>
                    </a>
                  </motion.div>
                ))}
              </nav>

              {/* Social Media Links */}
              <div className="mt-auto pb-10">
                <p className="dark:text-gray-500 text-zinc-900 text-base sm:text-lg">Follow me.</p>
                <div className="mt-4 sm:mt-8 flex flex-wrap gap-6 sm:gap-12">
                  {[
                    { name: "INSTAGRAM", href: "https://www.instagram.com/vivekk.dev/" },
                    { name: "LINKEDIN", href: "https://www.linkedin.com/in/vivek-khule-237682250/" },
                    { name: "TWITTER", href: "https://x.com/VivekKhule" },
                    { name: "GITHUB", href: "https://github.com/VKoder" },
                  ].map((social) => (
                    <motion.a
                      key={social.name}
                      href={social.href}
                      target='_blank'

                      className="group flex items-center gap-2 dark:hover:text-white dark:text-zinc-300 text-zinc-800 hover:text-zinc-900 transition-colors"
                      whileHover={{ x: 4 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      {social.name}
                      <motion.span
                        initial={{ opacity: 0, x: -5 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="inline-block"
                      >
                        <MdOutlineArrowForward className="w-4 h-4" />
                      </motion.span>
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}



const scrollbarHideStyles = `
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

export default function HeaderWithStyles(props) {
  return (
    <>
      <style jsx global>{scrollbarHideStyles}</style>
      <Header {...props} />
    </>
  );
}

